/**
 * Utility for handling retries with exponential backoff and jitter for API requests.
 */

/**
 * Options for configuring retry behavior
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds (default: 500ms) */
  baseDelay?: number;
  /** Maximum delay in milliseconds (default: 5000ms) */
  maxDelay?: number;
  /** 
   * Function to determine if a response should trigger a retry
   * By default, retries on 5xx status codes
   */
  shouldRetry?: (error: any) => boolean;
}

/**
 * Adds jitter to the retry delay to prevent thundering herd issues
 * @param delay - Base delay in milliseconds
 * @returns A modified delay with random jitter
 */
function addJitter(delay: number): number {
  // Add between 0-20% jitter
  const jitter = delay * 0.2 * Math.random();
  return delay + jitter;
}

/**
 * Executes a function with retry logic using exponential backoff and jitter.
 * 
 * @param fn - Async function to execute and potentially retry
 * @param options - Retry configuration options
 * @returns Promise resolving to the function's result
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const maxRetries = options.maxRetries ?? 3;
  const baseDelay = options.baseDelay ?? 500;
  const maxDelay = options.maxDelay ?? 5000;
  
  // Default shouldRetry function checks for 5xx status codes
  const shouldRetry = options.shouldRetry ?? ((error) => {
    if (error?.status >= 500 && error?.status < 600) return true;
    if (error?.response?.status >= 500 && error?.response?.status < 600) return true;
    return false;
  });

  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if we've hit the max attempts or if this error isn't retryable
      if (attempt >= maxRetries || !shouldRetry(error)) {
        throw error;
      }
      
      // Calculate exponential backoff with jitter
      const delay = Math.min(addJitter(baseDelay * Math.pow(2, attempt)), maxDelay);
      
      console.log(`Retrying API request, attempt ${attempt + 1}/${maxRetries} after ${delay}ms delay`);
      
      // Wait before the next attempt
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // This should never happen due to the throw inside the loop,
  // but TypeScript requires a return or throw at the end
  throw lastError;
} 